import { useInfiniteQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import { listNeedsReauth } from '@/api/ops';
import type { NeedsReauthAccount } from '@/api/opsTypes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/ui/DataTable';
import { KeysetMore } from '@/ui/KeysetMore';
import { QueryError } from '@/ui/QueryError';

const columns: ColumnDef<NeedsReauthAccount, never>[] = [
  { accessorKey: 'platform', header: 'Platform' },
  {
    id: 'org',
    header: 'Organization',
    cell: ({ row }) => (
      <Link to={`/orgs/${row.original.org_id}`} className="text-primary hover:underline">
        {row.original.org_id}
      </Link>
    ),
  },
  { accessorKey: 'social_account_id', header: 'Account id' },
  {
    id: 'since',
    header: 'Needing re-auth since',
    cell: ({ row }) => new Date(row.original.since).toLocaleString(),
  },
];

export function ReauthSection({ total }: { total: number }) {
  const query = useInfiniteQuery({
    queryKey: ['maintenance', 'needs-reauth'],
    queryFn: ({ pageParam }) => listNeedsReauth(pageParam),
    initialPageParam: undefined as { after: string; after_id: string } | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Accounts needing re-auth ({total})</CardTitle>
        <CardDescription>
          Only an account&apos;s owner can reconnect it, through the connect flow. Most recently
          broken first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isError ? (
          <QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={query.data?.pages.flatMap((page) => page.items) ?? []}
              getRowId={(row) => row.social_account_id}
              isLoading={query.isPending}
              empty="No account is waiting for re-auth."
            />
            <KeysetMore
              hasMore={query.hasNextPage}
              isFetching={query.isFetchingNextPage}
              onMore={() => void query.fetchNextPage()}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
